/**
 * Chemotion host adapter for chemkit bridge internals.
 * This is the only place that should touch ElementStore/ElementActions/UIStore/UserStore directly.
 */

/**
 * @typedef {'reaction'|'sample'} EntityKind
 */

/**
 * @typedef {Object} HostTabsState
 * @property {Array<any>} selecteds
 * @property {number} activeKey
 */

/**
 * @typedef {Object} HostEntityPools
 * @property {Array<any>} selecteds
 * @property {any|null} currentElement
 * @property {Array<any>} reactionElements
 * @property {Array<any>} sampleElements
 */

/**
 * @typedef {Object} ChemotionHostAdapter
 * @property {() => HostTabsState} readTabsState
 * @property {() => HostEntityPools} readEntityPools
 * @property {() => number|null} getCurrentUserId
 * @property {() => any} getActiveCollection
 * @property {() => any|null} readAutoSaveConfig
 * @property {(reactionId: string|number) => boolean} openReaction
 * @property {(reaction: any, options?: { background?: boolean }) => void} queueReactionSave
 * @property {(sample: any, options?: { background?: boolean }) => void} queueSampleSave
 * @property {(sample: any) => any} normalizeSampleForSave
 * @property {(source: string) => void} refreshTabs
 * @property {() => boolean} isAutosaveDebugEnabled
 * @property {(...args: Array<any>) => void} logAutosave
 * @property {(state: any) => ('open'|'review_pending'|'signed_off'|null)} normalizeReviewState
 * @property {(reactionId: string|number) => ('open'|'review_pending'|'signed_off'|null)} getRememberedReviewState
 * @property {(reactionId: string|number, state: any) => void} rememberReviewState
 * @property {(detail: any) => ({reactionId: number, state: 'open'|'review_pending'|'signed_off', isLocked: boolean}|null)} parseReviewStateChangedPayload
 */

/**
 * @param {Object} deps
 * @param {any} deps.windowRef
 * @param {any} deps.ElementStore
 * @param {any} deps.ElementActions
 * @param {any} deps.UIStore
 * @param {any} deps.UserStore
 * @param {any} deps.Sample
 * @param {string} deps.autosaveConfigWindowKey
 * @param {string} deps.autosaveRuntimeWindowKey
 * @param {string} deps.refreshSourceTag
 * @param {(state: any) => ('open'|'review_pending'|'signed_off'|null)} deps.normalizeReviewState
 * @param {(reactionId: string|number) => ('open'|'review_pending'|'signed_off'|null)} deps.getRememberedReviewState
 * @param {(reactionId: string|number, state: any) => void} deps.rememberReviewState
 * @param {(detail: any) => ({reactionId: number, state: 'open'|'review_pending'|'signed_off', isLocked: boolean}|null)} deps.parseReviewStateChangedPayload
 * @returns {ChemotionHostAdapter}
 */
export function createChemotionHostAdapter({
  windowRef,
  ElementStore,
  ElementActions,
  UIStore,
  UserStore,
  Sample,
  autosaveConfigWindowKey,
  autosaveRuntimeWindowKey,
  refreshSourceTag,
  normalizeReviewState,
  getRememberedReviewState,
  rememberReviewState,
  parseReviewStateChangedPayload,
}) {
  const readTabsState = () => {
    const { selecteds = [], activeKey = 0 } = ElementStore.getState();
    return { selecteds, activeKey };
  };

  const readEntityPools = () => {
    const { selecteds = [], currentElement = null, elements = {} } = ElementStore.getState();
    const reactionElements = elements.reactions && Array.isArray(elements.reactions.elements)
      ? elements.reactions.elements
      : [];
    const sampleElements = elements.samples && Array.isArray(elements.samples.elements)
      ? elements.samples.elements
      : [];

    return {
      selecteds,
      currentElement,
      reactionElements,
      sampleElements,
    };
  };

  const getCurrentUserId = () => {
    const { currentUser = null } = UserStore.getState();
    return currentUser && typeof currentUser.id === 'number' ? currentUser.id : null;
  };

  const getActiveCollection = () => {
    const { currentCollection = null } = UIStore.getState();
    return currentCollection;
  };

  const readAutoSaveConfig = () => {
    const runtime = windowRef[autosaveRuntimeWindowKey];
    if (runtime && typeof runtime.readConfig === 'function') {
      return runtime.readConfig();
    }
    return windowRef[autosaveConfigWindowKey] || null;
  };

  const openReaction = (reactionId) => {
    const numericReactionId = Number.parseInt(String(reactionId), 10);
    if (!Number.isFinite(numericReactionId) || numericReactionId <= 0) {
      return false;
    }

    if (!ElementActions || typeof ElementActions.fetchReactionById !== 'function') {
      return false;
    }

    ElementActions.fetchReactionById(numericReactionId);
    return true;
  };

  const queueReactionSave = (reaction, options = {}) => {
    windowRef.__CHEMKIT_SAVE_TRIGGER__ = options.trigger || undefined;
    try {
      ElementActions.updateReaction(reaction, false, options.background === true);
    } finally {
      delete windowRef.__CHEMKIT_SAVE_TRIGGER__;
    }
  };

  const normalizeSampleForSave = (sample) => {
    if (sample && typeof sample.cleanBoilingMelting === 'function') {
      sample.cleanBoilingMelting();
    }
    return new Sample(sample);
  };

  const queueSampleSave = (sample, options = {}) => {
    const normalized = normalizeSampleForSave(sample);
    windowRef.__CHEMKIT_SAVE_TRIGGER__ = options.trigger || undefined;
    try {
      ElementActions.updateSample(normalized, false, options.background === true);
    } finally {
      delete windowRef.__CHEMKIT_SAVE_TRIGGER__;
    }
  };

  const refreshTabs = (source = refreshSourceTag) => {
    ElementActions.chemkitUiTabsUpdated({ source });
  };

  const isAutosaveDebugEnabled = () =>
    Boolean(windowRef[autosaveConfigWindowKey] && windowRef[autosaveConfigWindowKey].debug === true);

  const logAutosave = (...args) => {
    if (isAutosaveDebugEnabled()) {
      // eslint-disable-next-line no-console
      console.log('[autosave]', ...args);
    }
  };

  return {
    readTabsState,
    readEntityPools,
    getCurrentUserId,
    getActiveCollection,
    readAutoSaveConfig,
    openReaction,
    queueReactionSave,
    queueSampleSave,
    normalizeSampleForSave,
    refreshTabs,
    isAutosaveDebugEnabled,
    logAutosave,
    normalizeReviewState,
    getRememberedReviewState,
    rememberReviewState,
    parseReviewStateChangedPayload,
  };
}
